import GVSearchSelect from 'components/lib/GVSearchSelect/GVSearchSelect';
import { SquarePen } from '@globalvisioninc/assets/icons';
import {
  useCustomProfiles,
  useCustomProfileById,
  useUpdateCustomProfile,
} from 'store/queries/organizations/useCustomProfiles';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId, isAdmin, isCustomProfiler, isTenantAdmin } from 'utils/auth';
import useIsInternalOrg from 'hooks/useIsInternalOrg';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTracker } from 'components/Tracker/TrackerProvider';
import { getBarcodeProfile, getInspectionOriginalOrgId, inspection } from 'store';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProfileEditorModal from 'components/common/ProfileEditor/ProfileEditorModal';
import {
  formatBarcodeProfileToIni,
  parseIniToBarcodeProfileFields,
  getEditedFields,
} from 'pages/MyAccount/customProfiles/profileFormatter';
import { BarcodeProfileFields } from 'pages/MyAccount/customProfiles/profileFormatter';
import { ProfileFields } from 'pages/MyAccount/customProfiles/profileFormatter';
import styles from './BarcodeMenu.module.scss';

type CustomProfile = {
  id: string;
  customProfileName: string;
  profileType?: 'graphic' | 'barcode';
};

const BarcodeMenu = () => {
  const tracker = useTracker();
  const dispatch = useDispatch();
  const barcodeProfile = useSelector(getBarcodeProfile);
  const originalOrgId = useSelector(getInspectionOriginalOrgId);
  const { user } = useUserStateStore();
  const userOrgId = getOrganizationId(user);
  const featureFlags = useFlags();
  const isInternalOrg = useIsInternalOrg();
  const canEditProfiles =
    isCustomProfiler(user) ||
    ((isAdmin(user) || isTenantAdmin(user)) && (isInternalOrg || featureFlags.showUsersCustomProfiles));
  const updateCustomProfileMutation = useUpdateCustomProfile();

  const [profileIdToEdit, setProfileIdToEdit] = useState<string | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { data: profileToEdit } = useCustomProfileById(profileIdToEdit);

  const handleEditClick = (profileId: string) => {
    setProfileIdToEdit(profileId);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setProfileIdToEdit(undefined);
  };

  const handleEditSave = (profileName: string, fields: ProfileFields | BarcodeProfileFields) => {
    if (!profileToEdit) return;
    const barcodeFields = fields as BarcodeProfileFields;
    const profileString = formatBarcodeProfileToIni(barcodeFields);
    updateCustomProfileMutation.mutate(
      {
        ...profileToEdit,
        customProfileName: profileName,
        customProfileString: profileString,
        rasterDPI: barcodeFields.rasterDPI ? Number(barcodeFields.rasterDPI) : undefined,
        barcodeReRasterDpi: barcodeFields.barcodeReRasterDpi ? Number(barcodeFields.barcodeReRasterDpi) : undefined,
      },
      {
        onSuccess: () => {
          const originalFields = {
            ...parseIniToBarcodeProfileFields(profileToEdit.customProfileString ?? ''),
            // DPI values are not in the XML so we merge them from the saved profile
            rasterDPI: profileToEdit.rasterDPI ? String(profileToEdit.rasterDPI) : undefined,
            barcodeReRasterDpi: profileToEdit.barcodeReRasterDpi ? String(profileToEdit.barcodeReRasterDpi) : undefined,
          };
          tracker.track({
            name: 'custom-profile-edited',
            data: {
              profileId: profileToEdit.id,
              profileName,
              profileType: 'barcode',
              fieldsEdited: getEditedFields(barcodeFields, originalFields),
            },
          });
          handleEditClose();
        },
      },
    );
  };

  // Check if inspection is cloned from a different organization
  const isClonedFromDifferentOrg = useMemo(() => {
    if (!originalOrgId || !userOrgId) {
      return false;
    }
    return originalOrgId !== userOrgId;
  }, [originalOrgId, userOrgId]);

  // Get additional profile ID when cloned from different org
  const additionalProfileId = useMemo(() => {
    if (!isClonedFromDifferentOrg || !barcodeProfile) {
      return undefined;
    }
    // Check if barcodeProfile is a custom profile ID (not 'Default')
    if (barcodeProfile !== 'Default') {
      return barcodeProfile;
    }
    return undefined;
  }, [isClonedFromDifferentOrg, barcodeProfile]);

  const { customProfilesList } = useCustomProfiles(undefined, additionalProfileId);

  // Filter to only show barcode profiles
  const barcodeProfiles = useMemo(() => {
    return customProfilesList.filter((profile: CustomProfile) => (profile.profileType || 'graphic') === 'barcode');
  }, [customProfilesList]);

  // Don't render if no barcode profiles
  if (barcodeProfiles.length === 0) {
    return null;
  }

  const handleProfileChange = (selectedValue: string) => {
    const selectedProfile = barcodeProfiles.find((p: CustomProfile) => p.id === selectedValue);
    const profileName = selectedProfile ? selectedProfile.customProfileName : 'Default';

    dispatch(
      inspection.actions.setBarcodeSettings({
        customProfileId: selectedValue,
        customProfileName: profileName,
      }),
    );
  };

  return (
    <>
      <div className={styles.list}>
        <GVSearchSelect
          className={styles.field}
          value={barcodeProfile || 'Default'}
          label="Barcode Profile"
          onValueChange={handleProfileChange}
          searchPlaceholder="Search profiles..."
          sections={[
            {
              label: 'Profile',
              searchable: false,
              options: [{ value: 'Default', label: 'Default' }],
            },
            ...(barcodeProfiles.length > 0
              ? [
                  {
                    label: 'Custom Profiles',
                    options: barcodeProfiles.map((p: CustomProfile) => ({
                      value: p.id,
                      label: p.customProfileName,
                      ...(canEditProfiles
                        ? {
                            action: {
                              icon: SquarePen,
                              label: `Edit ${p.customProfileName}`,
                              onClick: () => handleEditClick(p.id),
                            },
                          }
                        : {}),
                    })),
                  },
                ]
              : []),
          ]}
        />
      </div>
      {canEditProfiles && (
        <ProfileEditorModal
          open={editModalOpen}
          onClose={handleEditClose}
          onSave={handleEditSave}
          profile={profileToEdit ?? null}
          profileType="barcode"
          initialProfileName={profileToEdit?.customProfileName ?? ''}
        />
      )}
    </>
  );
};

export default BarcodeMenu;
